import React, { useRef, useState, useEffect } from 'react';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import useModalEscape from '../../hooks/useModalEscape';
import useModalTransition from '../../hooks/useModalTransition';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../../components/Sidebar/Sidebar';
import ScopeLimitationsModal from '../../components/ScopeLimitationsModal/ScopeLimitationsModal';
import TermsOfServiceModal from '../../components/LegalModals/TermsOfServiceModal';
import PrivacyPolicyModal from '../../components/LegalModals/PrivacyPolicyModal';
import './SA_Settings.css';
import { API_BASE_URL } from '../../config';
import { notifyError, notifySuccess } from '../../notify';

// Import lucide-react icons
import { 
  Edit2,
  Lock,
  Globe,
  Binoculars,
  Info,
  Shield,
  X,
  User,
  Mail,
  AlertCircle,
  CheckCircle,
  Send,
  ChevronRight,
  Check,
  ExternalLink
} from 'lucide-react';

const SETTINGS_PROFILE_CACHE_KEY = 'avisa.settingsProfile';
const SETTINGS_PROFILE_PHOTO_CACHE_PREFIX = 'avisa.settingsProfilePhoto.';
const SETTINGS_PROFILE_LAST_AVATAR_KEY = 'avisa.settingsProfileLastAvatar';

let lastSettingsAvatarSrc = (() => {
  try {
    return localStorage.getItem(SETTINGS_PROFILE_LAST_AVATAR_KEY);
  } catch {
    return null;
  }
})();

const rememberSettingsAvatar = (src) => {
  if (!src) return;
  lastSettingsAvatarSrc = src;
  try {
    localStorage.setItem(SETTINGS_PROFILE_LAST_AVATAR_KEY, src);
  } catch {
    // Ignore storage limits/private mode and keep the in-memory avatar.
  }
};

const readCachedProfilePhoto = (cacheKey) => {
  if (!cacheKey) return null;
  try {
    return localStorage.getItem(`${SETTINGS_PROFILE_PHOTO_CACHE_PREFIX}${cacheKey}`);
  } catch {
    return null;
  }
};

const readLastGoodAvatar = (cacheKey, cachedProfile, fallbackUser) =>
  readCachedProfilePhoto(cacheKey) ||
  lastSettingsAvatarSrc ||
  cachedProfile?.profilePhoto ||
  fallbackUser?.profilePhoto ||
  fallbackUser?.profile?.profilePhoto ||
  null;

const saveProfilePhotoCopy = async (cacheKey, photoUrl) => {
  if (!cacheKey || !photoUrl || photoUrl.startsWith('data:')) return null;

  try {
    const response = await fetch(photoUrl);
    if (!response.ok) return null;

    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = typeof reader.result === 'string' ? reader.result : null;
        if (dataUrl) {
          try {
            localStorage.setItem(`${SETTINGS_PROFILE_PHOTO_CACHE_PREFIX}${cacheKey}`, dataUrl);
          } catch {
            // Ignore storage limits/private mode and keep using the remote URL.
          }
          rememberSettingsAvatar(dataUrl);
        }
        resolve(dataUrl);
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// Edit Profile Modal Component
const EditProfileModal = ({ isOpen, onClose, onProfileUpdate, user, getIdToken, currentUser }) => {
  const shouldRender = useModalTransition(isOpen);
  const { updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    username: ''
  });
  
  useEffect(() => {
    if (isOpen) {
      fetchUserProfile();
    }
  }, [isOpen]);
  
  const fetchUserProfile = async () => {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.ok && data.profile) {
          setProfileData({
            name: data.profile.name || '',
            username: data.profile.username || ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };
  
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: profileData.username
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.ok) {
        notifySuccess('Profile updated successfully!');
        if (onProfileUpdate) onProfileUpdate(data.profile);
        if (updateProfile) updateProfile(data.profile);
        onClose();
      } else {
        notifyError(data.error || 'Failed to update profile');
      }
    } catch (error) {
      notifyError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (!shouldRender) return null;
  
  return (
    <div className={`edit-profile-modal-overlay modal-transition-${isOpen ? 'enter' : 'exit'}`} onClick={onClose}>
      <div className="edit-profile-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="edit-profile-modal-close" onClick={onClose}>
          <X size={20} />
        </button>
        
        <h2 className="edit-profile-modal-title">Edit Profile</h2>
        
        <div className="edit-profile-modal-body">
          <form onSubmit={handleProfileUpdate}>
            <div className="edit-profile-form-field">
              <label className="edit-profile-field-label">Username</label>
              <div className="edit-profile-input-wrapper">
                <span className="edit-profile-input-icon">
                  <User size={18} />
                </span>
                <input
                  type="text"
                  className="edit-profile-field-input"
                  value={profileData.username}
                  onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                  placeholder="Enter username"
                  required
                />
              </div>
            </div>
            
            <div className="edit-profile-button-container">
              <button 
                type="submit" 
                className="edit-profile-btn"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Password Reset Modal Component
const PasswordResetModal = ({ isOpen, onClose, userEmail, getIdToken }) => {
  const shouldRender = useModalTransition(isOpen);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
    }
  }, [isOpen]);

  const handlePasswordReset = async () => {
    if (!userEmail) {
      notifyError('No email found for this account.');
      return;
    }

    setLoading(true);
    
    try {
      const token = await getIdToken?.();
      const response = await fetch(`${API_BASE_URL}/api/auth/send-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          email: userEmail,
          idToken: token || undefined
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.ok) {
        notifySuccess('Password reset email sent. The link will expire in 1 hour.');
        onClose();
      } else {
        notifyError(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      notifyError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!shouldRender) return null;

  return (
    <div className={`password-reset-modal-overlay modal-transition-${isOpen ? 'enter' : 'exit'}`} onClick={onClose}>
      <div className="password-reset-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="password-reset-modal-close" onClick={onClose}>
          <X size={20} />
        </button>
        
        <div className="password-reset-icon-wrapper">
          <div className="password-reset-icon-circle">
            <Lock size={32} />
          </div>
        </div>
        
        <h2 className="password-reset-modal-title">Reset Password</h2>
        
        <p className="password-reset-description">
          We'll send a password reset link to your registered email address. 
          Click the link in the email to create a new password.
        </p>
        
        <div className="password-reset-email-info">
          <Mail size={16} />
          <span>Sending to: <strong>{userEmail}</strong></span>
        </div>
        
        <div className="password-reset-button-container">
          <button 
            className="password-reset-btn"
            onClick={handlePasswordReset}
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </div>
          <div className="password-reset-footer">
          <p>
            Make sure to check your spam folder if you don't see the email within a few minutes.
          </p>
        </div>
      </div>
    </div>
  );
};

// About Avisa Modal
const AboutModal = ({ isOpen, onClose }) => {
  const shouldRender = useModalTransition(isOpen);
  if (!shouldRender) return null;

  return (
    <div className={`about-modal-overlay modal-transition-${isOpen ? 'enter' : 'exit'}`} onClick={onClose}>
      <div className="about-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="about-modal-close" onClick={onClose}>
          <X size={20} />
        </button>
        
        <div className="privacy-header">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <h2 className="privacy-title" style={{ margin: 0 }}>About Avisa</h2>
          </div>
          <p className="privacy-subtitle">Our mission and team</p>
        </div>
        
        <div className="about-body">
          <div className="about-section">
            <p>Avisa is an AI-Enhanced Legal Information Management System designed to bridge the legal literacy gap in the Philippines by making national laws and local ordinances accessible to every Filipino citizen. Developed as a capstone project by students from National University, the platform transforms complex legal documents into plain-language summaries and Filipino translations using Google's Gemini AI, while preserving legal accuracy through expert verification. The system features a jurisdiction-aware search that prioritizes relevant barangay and municipal ordinances, a RAG-powered chatbot that answers legal questions with cited sources, and direct access to original scanned documents for verification. Built with a three-tier cloud-native architecture using React Native, React.js, and Firebase, Avisa serves both citizens seeking to understand their rights and Local Government Units needing efficient ordinance management tools. The platform aligns with UN Sustainable Development Goal 16 by promoting equal access to justice and strengthening transparency between the public and local institutions.</p>
          </div>
          
          <div className="about-footer">
            <p>&copy; 2026 Avisa. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// ... (rest of the code remains the same)

// TermsOfServiceModal and PrivacyPolicyModal imported from LegalModals

const SA_Settings = () => {
  useDocumentTitle("Avisa Admin Dashboard");

  const navigate = useNavigate();
  const { logout: authLogout, user, getIdToken } = useAuth();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false);
  const [isScopeModalOpen, setIsScopeModalOpen] = useState(false);
  const [isTermsOfServiceModalOpen, setIsTermsOfServiceModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const fetchedProfileKeyRef = useRef(null);
  const userProfileCacheKey = user?.uid || user?.email || 'current-user';

  const getUserInitial = (usernameValue, nameValue, emailValue) => {
    const source = String(usernameValue || nameValue || emailValue || 'U').trim();
    return source ? source.charAt(0).toUpperCase() : 'U';
  };

  const buildSettingsUser = (profile, fallbackUser = user) => {
    const rawRole = profile?.role || fallbackUser?.role || '';
    const normalizedRole = (rawRole === 'system_admin' || rawRole === 'sysadmin') ? 'System Admin' : 'Admin';
    const email = profile?.email || fallbackUser?.email || '';
    const username = profile?.username || fallbackUser?.username || 'User';
    const fullName = profile?.name || fallbackUser?.name || 'User';

    return {
      username,
      fullName,
      email,
      role: normalizedRole,
      avatarInitial: getUserInitial(username, fullName, email),
      profilePhoto: profile?.profilePhoto || fallbackUser?.profilePhoto || fallbackUser?.profile?.profilePhoto || null
    };
  };

  const getCachedSettingsUser = () => {
    try {
      const cached = localStorage.getItem(SETTINGS_PROFILE_CACHE_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  };

  const saveCachedSettingsUser = (profile) => {
    try {
      localStorage.setItem(SETTINGS_PROFILE_CACHE_KEY, JSON.stringify(profile));
    } catch {
      // Ignore storage limits/private mode and keep the in-memory profile.
    }
  };

  const cachedSettingsUser = getCachedSettingsUser();
  const [currentUser, setCurrentUser] = useState(() => cachedSettingsUser || buildSettingsUser(null, user));
  const [avatarSrc, setAvatarSrc] = useState(
    () => readLastGoodAvatar(userProfileCacheKey, cachedSettingsUser, user)
  );

  const closeAllModals = () => {
    setIsEditModalOpen(false);
    setIsPasswordModalOpen(false);
    setIsAboutModalOpen(false);
    setIsScopeModalOpen(false);
    setIsTermsOfServiceModalOpen(false);
    setIsPrivacyModalOpen(false);
  };

  useModalEscape(closeAllModals, isEditModalOpen || isPasswordModalOpen || isAboutModalOpen || isScopeModalOpen || isTermsOfServiceModalOpen || isPrivacyModalOpen);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;

      const profileKey = user.uid || user.email || 'current-user';
      if (fetchedProfileKeyRef.current === profileKey) return;
      fetchedProfileKeyRef.current = profileKey;

      try {
        const token = await getIdToken();
        const response = await fetch(`${API_BASE_URL}/api/users/profile`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.ok && data.profile) {
            const nextUser = buildSettingsUser(data.profile, user);
            setCurrentUser(prev => {
              const stableUser = {
                ...nextUser,
                profilePhoto: nextUser.profilePhoto || prev?.profilePhoto || null
              };
              saveCachedSettingsUser(stableUser);
              if (
                prev?.username === stableUser.username &&
                prev?.fullName === stableUser.fullName &&
                prev?.email === stableUser.email &&
                prev?.role === stableUser.role &&
                prev?.profilePhoto === stableUser.profilePhoto
              ) {
                return prev;
              }
              return stableUser;
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setCurrentUser(prev => prev || buildSettingsUser(null, user));
      }
    };
    
    fetchUserData();
  }, [user?.uid, user?.email]);

  useEffect(() => {
    const photoUrl = currentUser?.profilePhoto || user?.profilePhoto || user?.profile?.profilePhoto || null;
    const cachedPhoto = readLastGoodAvatar(userProfileCacheKey, currentUser, user);

    if (cachedPhoto) {
      setAvatarSrc(cachedPhoto);
      rememberSettingsAvatar(cachedPhoto);
      return;
    }

    setAvatarSrc(prev => {
      const nextAvatar = prev || photoUrl;
      rememberSettingsAvatar(nextAvatar);
      return nextAvatar;
    });

    let isCancelled = false;
    saveProfilePhotoCopy(userProfileCacheKey, photoUrl).then((savedPhoto) => {
      if (!isCancelled && savedPhoto) {
        setAvatarSrc(savedPhoto);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [currentUser?.profilePhoto, user?.profilePhoto, user?.profile?.profilePhoto, userProfileCacheKey]);

  const handleLogout = async () => {
    await authLogout();
    navigate('/admin', { replace: true });
  };

  const handleProfileUpdate = (updatedProfile) => {
    setCurrentUser(prev => ({
      ...prev,
      username: updatedProfile.username || prev.username,
      fullName: updatedProfile.name || prev.fullName,
      avatarInitial: getUserInitial(
        updatedProfile.username || prev.username,
        updatedProfile.name || prev.fullName,
        prev.email
      ),
      profilePhoto: updatedProfile.profilePhoto || prev.profilePhoto
    }));

    const nextUser = {
      ...(currentUser || buildSettingsUser(null, user)),
      username: updatedProfile.username || currentUser?.username,
      fullName: updatedProfile.name || currentUser?.fullName,
      profilePhoto: updatedProfile.profilePhoto || currentUser?.profilePhoto || user?.profilePhoto || null
    };
    saveCachedSettingsUser(nextUser);
    rememberSettingsAvatar(nextUser.profilePhoto);
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      
      <main className="main-content settings-page">
        <div className="settings-header-bar">
          <h1 className="settings-title">Settings</h1>
          <button className="logout-btn" onClick={handleLogout}>
            Log out
          </button>
        </div>

        {/* Profile card */}
        <div className="user-profile-card">
          <div className="user-avatar" style={{ overflow: 'hidden' }}>
            {avatarSrc ? (
              <img 
                src={avatarSrc} 
                alt="Profile" 
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
              />
            ) : (
              getUserInitial(currentUser?.username || user?.username, currentUser?.fullName || user?.name, currentUser?.email || user?.email)
            )}
          </div>
          <div className="user-info">
            <div className="username">{currentUser?.fullName || 'User'}</div>
            <div className="user-role">{currentUser?.role || 'Admin'}</div>
          </div>
          <button className="edit-btn" onClick={() => setIsEditModalOpen(true)}>
            <Edit2 size={20} />
          </button>
        </div>

        {/* General Settings */}
        <div className="settings-section">
          <h2 className="section-title">General</h2>

          <div className="settings-item" onClick={() => setIsPasswordModalOpen(true)}>
            <Lock size={20} className="item-icon" />
            <div className="item-content">
              <span className="item-text">Change Password</span>
              <span className="item-value">Reset your password via email</span>
            </div>
          </div>
        </div>

        {/* Info Settings */}
        <div className="settings-section">
          <h2 className="section-title">Info</h2>

          <div className="settings-item" onClick={() => setIsAboutModalOpen(true)}>
            <Info size={20} className="item-icon" />
            <div className="item-content">
              <span className="item-text">About Avisa</span>
              <span className="item-value">Team Credits and Information</span>
            </div>
            <ChevronRight size={16} className="item-arrow" />
          </div>

          <div className="settings-item" onClick={() => setIsScopeModalOpen(true)}>
            <Binoculars size={20} className="item-icon" />
            <div className="item-content">
              <span className="item-text">Scope &amp; Limitations</span>
              <span className="item-value">Project coverage and boundaries</span>
            </div>
            <ChevronRight size={16} className="item-arrow" />
          </div>

          <div className="settings-item" onClick={() => setIsTermsOfServiceModalOpen(true)}>
            <Globe size={20} className="item-icon" />
            <div className="item-content">
              <span className="item-text">Terms of Service</span>
              <span className="item-value">Our terms and conditions</span>
            </div>
            <ChevronRight size={16} className="item-arrow" />
          </div>

          <div className="settings-item" onClick={() => setIsPrivacyModalOpen(true)}>
            <Shield size={20} className="item-icon" />
            <div className="item-content">
              <span className="item-text">Privacy Policy</span>
              <span className="item-value">How we handle user's data</span>
            </div>
            <ChevronRight size={16} className="item-arrow" />
          </div>
        </div>
      </main>

      {/* Modals */}
      <EditProfileModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onProfileUpdate={handleProfileUpdate}
        user={user}
        getIdToken={getIdToken}
        currentUser={currentUser}
      />
      
      <PasswordResetModal 
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        userEmail={currentUser?.email || user?.email || ''}
        getIdToken={getIdToken}
      />
      
      <AboutModal 
        isOpen={isAboutModalOpen}
        onClose={() => setIsAboutModalOpen(false)}
      />

      <ScopeLimitationsModal
        isOpen={isScopeModalOpen}
        onClose={() => setIsScopeModalOpen(false)}
      />
      
      <TermsOfServiceModal 
        isOpen={isTermsOfServiceModalOpen}
        onClose={() => setIsTermsOfServiceModalOpen(false)}
      />
      
      <PrivacyPolicyModal 
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
      />
    </div>
  );
};

export default SA_Settings;
